import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, Smartphone, User } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('username'); // 'username' or 'otp'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setMessage('Please enter a username');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/auth/send-otp', { username });
      
      if (response.data.success) {
        setStep('otp');
        // API always returns success for unknown users (anti-enumeration).
        // Real OTP only arrives if that username exists and the bot can message you.
        setMessage(
          response.data.message ||
            'If that username exists, an OTP was sent to Telegram.'
        );
        setMessageType('success');
      } else {
        setMessage(response.data.message || 'Failed to send OTP');
        setMessageType('error');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to send OTP');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setMessage('Please enter the OTP');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/auth/verify-otp', { 
        username, 
        otp 
      });
      
      if (response.data.success) {
        onLogin(response.data.user, response.data.token);
        
        // Check if user needs to set up username
        if (!response.data.user.username) {
          navigate('/setup-username');
        } else {
          navigate('/dashboard');
        }
      } else {
        setMessage(response.data.message || 'Invalid OTP');
        setMessageType('error');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to verify OTP');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '400px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <MessageCircle size={48} color="#2d3748" />
          <h1 style={{ marginTop: '1rem' }}>
            Expense Tracker
          </h1>
          <p style={{ color: '#4a5568', marginTop: '0.5rem' }}>
            Login with your username
          </p>
        </div>

        {message && (
          <div className={`alert alert-${messageType}`}>
            {message}
          </div>
        )}

        {step === 'username' ? (
          <form onSubmit={handleSendOTP}>
            <div className="form-group">
              <label htmlFor="username">
                <User size={16} style={{ marginRight: '8px' }} />
                Username
              </label>
              <input
                type="text"
                id="username"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                disabled={loading}
              />
            </div>
            <button 
              type="submit" 
              className="btn" 
              style={{ width: '100%' }}
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP to Telegram'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div className="form-group">
              <label htmlFor="otp">
                <Smartphone size={16} style={{ marginRight: '8px' }} />
                OTP Code
              </label>
              <input
                type="text"
                id="otp"
                className="input"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                disabled={loading}
              />
            </div>
            <button 
              type="submit" 
              className="btn" 
              style={{ width: '100%', marginBottom: '1rem' }}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button 
              type="button" 
              className="btn" 
              style={{ 
                width: '100%', 
                background: 'transparent',
                color: '#2d3748',
                border: '2px solid rgba(45, 55, 72, 0.3)'
              }}
              onClick={() => setStep('username')}
              disabled={loading}
            >
              Back to Username
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login; 